import { ApiCaller } from '../api/api-caller';
import type { ModeDto } from '../types/dtos';
import type { ExternalStorage } from '../types/external-storage';
import type { WidgetConfig } from '../types/widget-config';
import { ActiveSessionPollingCtx } from './active-session-polling.ctx';
import { ContactCtx } from './contact.ctx';
import { UploadCtx } from './upload.ctx';
import { CsatCtx } from './csat.ctx';
import { DictationCtx } from './dictation.ctx';
import { MessageCtx } from './message.ctx';
import { RouterCtx } from './router.ctx';
import { SessionCtx } from './session.ctx';
import { StorageCtx } from './storage.ctx';
import { UnreadCtx } from './unread.ctx';
import {
  resolveClientFeatures,
  resolveWidgetAgent,
  type WidgetAgent,
  type WidgetClientFeatures,
} from './widget-agent';
import { log } from '../utils/log';

/** A configuration failure that prevents the widget from choosing a safe
 * runtime. The code is stable so headless consumers can render their own
 * recovery UI without matching error-message text. */
export class WidgetInitializationError extends Error {
  readonly code: 'config-fetch-failed';
  readonly details: unknown;

  constructor(code: 'config-fetch-failed', message: string, details?: unknown) {
    super(message);
    this.name = 'WidgetInitializationError';
    this.code = code;
    this.details = details;
  }
}

export class WidgetCtx {
  public config: WidgetConfig;
  public api: ApiCaller;

  public contactCtx: ContactCtx;
  public sessionCtx: SessionCtx;
  public messageCtx: MessageCtx;
  public csatCtx: CsatCtx;
  public uploadCtx: UploadCtx;
  public dictationCtx: DictationCtx;
  public routerCtx: RouterCtx;
  public storageCtx?: StorageCtx;
  /** Shared by every conversation runtime: the launcher has one dot. */
  public unreadCtx: UnreadCtx;
  public modes: ModeDto[] = [];

  public org: {
    id: string;
    name: string;
  };
  /**
   * The org's agent — branding plus which engine serves it — resolved by the
   * backend at init. The `bot` option overrides the branding at render time.
   */
  public readonly agent: WidgetAgent;

  /**
   * Choose the transport from the org's default and the current embed opt-out.
   * The backend chooses the agent version independently of this transport.
   */
  private readonly getRequestConfig: () => WidgetConfig;
  private readonly getClientCapabilities?: () => WidgetConfig['capabilities'];

  public get streaming(): boolean {
    // Accepted sends keep their transport through preparation, queue draining
    // and reconciliation. Never let a prop update detach their owner.
    if (this.messageCtx?.hasPendingAgentWork) return true;
    if (this.messageCtx?.state.get().isSendingMessage) return false;
    return this.agent.streaming && this.getRequestConfig().streaming === true;
  }

  /**
   * The org's features narrowed by this embed's `config.features` — the
   * answers the UI asks for, resolved against the current options.
   */
  public get features(): WidgetClientFeatures {
    return resolveClientFeatures(this.agent, this.getRequestConfig());
  }

  private static pollingIntervalsSeconds: {
    session: number;
    sessions: number;
  } | null = null;
  private activeSessionPollingCtx: ActiveSessionPollingCtx;
  private readonly ownsUnreadCtx: boolean;
  private disposed = false;

  private constructor({
    config,
    storage,
    getClientCapabilities,
    getRequestConfig,
    modes,
    org,
    agent,
    parent,
  }: {
    config: WidgetConfig;
    storage?: ExternalStorage;
    getClientCapabilities?: () => WidgetConfig['capabilities'];
    getRequestConfig?: () => WidgetConfig;
    modes: ModeDto[];
    org: {
      id: string;
      name: string;
    };
    agent: WidgetAgent;
    parent?: WidgetCtx;
  }) {
    if (!WidgetCtx.pollingIntervalsSeconds) {
      throw Error(
        'Widget polling values are not defined, did you call WidgetCtx.initialize()',
      );
    }

    this.config = config;
    this.getRequestConfig = getRequestConfig ?? (() => this.config);
    this.getClientCapabilities = getClientCapabilities;
    this.org = org;
    this.agent = agent;
    this.api = parent?.api ?? new ApiCaller({ config });
    this.storageCtx =
      parent?.storageCtx ??
      (storage ? new StorageCtx({ storage, config }) : undefined);
    this.modes = modes;

    this.contactCtx =
      parent?.contactCtx ??
      new ContactCtx({
        api: this.api,
        config: this.config,
        storageCtx: this.storageCtx,
      });

    this.sessionCtx = new SessionCtx({
      config: this.config,
      api: this.api,
      contactCtx: this.contactCtx,
      // Remembers the open conversation so a reload can return to it.
      storageCtx: parent ? undefined : this.storageCtx,
      sharedSessions: parent?.sessionCtx.sessionsState,
      sessionsPollingIntervalSeconds:
        WidgetCtx.pollingIntervalsSeconds.sessions,
    });

    this.ownsUnreadCtx = !parent;
    this.unreadCtx =
      parent?.unreadCtx ??
      new UnreadCtx({
        config: this.config,
        api: this.api,
        sessionsState: this.sessionCtx.sessionsState,
        setSessions: this.sessionCtx.setSessions,
      });

    this.messageCtx = new MessageCtx({
      config: this.config,
      api: this.api,
      sessionCtx: this.sessionCtx,
      contactCtx: this.contactCtx,
      // Streaming orgs send their turns over the AI SDK transport instead of
      // the blocking bot-chat send.
      streaming: this.streaming,
      isStreaming: () => this.streaming,
      sendsPageContext: this.features.pageContext,
      getSendsPageContext: () => this.features.pageContext,
      getClientCapabilities,
      getRequestConfig,
    });

    this.uploadCtx = new UploadCtx(this.api);
    this.csatCtx = new CsatCtx({
      api: this.api,
      sessionCtx: this.sessionCtx,
      messageCtx: this.messageCtx,
    });

    this.dictationCtx = new DictationCtx({
      api: this.api,
      config: this.config,
    });

    this.activeSessionPollingCtx = new ActiveSessionPollingCtx({
      api: this.api,
      config: this.config,
      sessionCtx: this.sessionCtx,
      messageCtx: this.messageCtx,
      sessionPollingIntervalSeconds: WidgetCtx.pollingIntervalsSeconds.session,
    });

    this.routerCtx = new RouterCtx(this);
  }

  static initialize = async ({
    config,
    storage,
    getClientCapabilities,
    getRequestConfig,
  }: {
    config: WidgetConfig;
    storage?: ExternalStorage;
    getClientCapabilities?: () => WidgetConfig['capabilities'];
    getRequestConfig?: () => WidgetConfig;
  }) => {
    const externalConfig = await new ApiCaller({
      config,
    }).getExternalWidgetConfig();

    if (!externalConfig.data) {
      // Surface the backend's reason instead of failing mutely.
      log.error('widget config fetch failed', externalConfig.error);
      throw new WidgetInitializationError(
        'config-fetch-failed',
        'Failed to fetch widget config',
        externalConfig.error,
      );
    }

    this.pollingIntervalsSeconds = {
      session: externalConfig.data?.sessionPollingIntervalSeconds || 10,
      sessions: externalConfig.data?.sessionsPollingIntervalSeconds || 60,
    };

    return new WidgetCtx({
      config,
      storage,
      getClientCapabilities,
      getRequestConfig,
      modes: externalConfig.data.modes || [],
      org: {
        id: externalConfig.data.org.id,
        name: externalConfig.data.org.name,
      },
      agent: resolveWidgetAgent(externalConfig.data),
    });
  };

  /** Independent send/history lifecycle, sharing the authenticated visitor and session list. */
  createConversation = (): WidgetCtx => {
    const conversation = new WidgetCtx({
      config: {
        ...this.config,
        router: {
          ...this.config.router,
          restoreLastSession: false,
          chatScreenOnly: false,
          goToChatIfNoSessions: false,
        },
      },
      modes: this.modes,
      org: this.org,
      agent: this.agent,
      parent: this,
      getRequestConfig: this.getRequestConfig,
      getClientCapabilities: this.getClientCapabilities,
    });
    conversation.routerCtx.state.setPartial({
      screen: this.contactCtx.shouldCollectData() ? 'welcome' : 'chat',
    });
    return conversation;
  };

  /** Release a closed companion runtime after its last send has settled. */
  releaseConversation = () => this.dispose();

  /** Permanently stop all polling, subscriptions and in-flight work. */
  dispose = ({ clearActiveSession = false } = {}) => {
    if (this.disposed) return;
    this.disposed = true;
    this.routerCtx.dispose();
    this.activeSessionPollingCtx.dispose();
    if (this.ownsUnreadCtx) this.unreadCtx.dispose();
    const cleanup = this.sessionCtx.dispose({ clearActiveSession });
    this.messageCtx.reset();
    this.uploadCtx.reset();
    this.dictationCtx.stop({ executeFinalCommand: false });
    return cleanup;
  };

  resetChat = () => {
    this.sessionCtx.reset();
    this.messageCtx.reset();
    this.uploadCtx.reset();
  };

  /**
   * Ingest the canonical rows the backend persisted for a streamed turn.
   * Called by the agent-chat surface when a turn finishes; the polling
   * context owns history mapping/dedupe, so this delegates to it.
   */
  reconcileAfterStream = (sessionId: string): Promise<void> =>
    this.activeSessionPollingCtx.reconcileAfterStream(sessionId);
}
